import { Timestamp, type DocumentData } from 'firebase/firestore'
import type { GuestSpot } from '@/types/models'

export const LOCAL_GUEST_SPOTS_COLLECTION = 'localGuestSpots'
export const LOCAL_GUEST_SPOTS_ARCHIVED_COLLECTION = 'localGuestSpotsArchived'

export function normalizeInstagramHandle(rawHandle: string | undefined): string | undefined {
  if (!rawHandle) return undefined

  const normalizedHandle = rawHandle.replace(/^@+/, '').trim().toLowerCase()
  return normalizedHandle || undefined
}

function parseFirestoreDate(value: unknown): Date {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return new Date(value)
  }
  return new Date()
}

function normalizeInstagramUsername(rawValue: unknown): string | undefined {
  if (typeof rawValue !== 'string') return undefined

  const normalizedValue = rawValue
    .trim()
    .replace(/^(?:guesting\s+(?:at|in)\s+)+/i, '')
    .replace(/^@+/, '')
    .toLowerCase()

  return normalizedValue || undefined
}

export function parseLocalGuestSpotDocument(
  documentId: string,
  data: DocumentData,
): GuestSpot {
  const tattooImageUrls = Array.isArray(data.tattooImageUrls)
    ? data.tattooImageUrls.filter((url): url is string => typeof url === 'string')
    : undefined
  const top4CompressedImageUrls = Array.isArray(data.top4CompressedImageUrls)
    ? data.top4CompressedImageUrls.filter((url): url is string => typeof url === 'string')
    : undefined

  const spot: GuestSpot = {
    id: documentId,
    targetCity: data.targetCity,
    targetCountry: data.targetCountry,
    startDate: parseFirestoreDate(data.startDate),
    endDate: parseFirestoreDate(data.endDate),
    createdAt: parseFirestoreDate(data.createdAt),
  }

  if (typeof data.username === 'string') {
    spot.username = data.username.replace(/^@+/, '').toLowerCase()
  }
  if (typeof data.profileImageUrl === 'string') {
    spot.profileImageUrl = data.profileImageUrl
  }
  if (typeof data.homeCity === 'string') {
    spot.homeCity = data.homeCity
  }
  if (typeof data.homeCountry === 'string') {
    spot.homeCountry = data.homeCountry
  }
  if (typeof data.homeLat === 'number') {
    spot.homeLat = data.homeLat
  }
  if (typeof data.homeLon === 'number') {
    spot.homeLon = data.homeLon
  }
  if (typeof data.targetLat === 'number') {
    spot.targetLat = data.targetLat
  }
  if (typeof data.targetLon === 'number') {
    spot.targetLon = data.targetLon
  }
  if (typeof data.artistId === 'string') {
    spot.artistId = data.artistId
  }
  if (typeof data.studioName === 'string') {
    spot.studioName = data.studioName
  }
  if (data.isVerified !== undefined) {
    spot.isVerified = data.isVerified
  }
  if (top4CompressedImageUrls?.length) {
    spot.top4CompressedImageUrls = top4CompressedImageUrls
  }
  if (tattooImageUrls?.length) {
    spot.tattooImageUrls = tattooImageUrls
  }
  if (typeof data.source === 'string') {
    spot.source = data.source
  }
  if (typeof data.sourcePostUrl === 'string') {
    spot.sourcePostUrl = data.sourcePostUrl
  }
  if (typeof data.sourcePostId === 'string') {
    spot.sourcePostId = data.sourcePostId
  }
  if (typeof data.pipeline === 'string') {
    spot.pipeline = data.pipeline
  }
  if (typeof data.spotStatus === 'string') {
    spot.spotStatus = data.spotStatus
  }
  if (typeof data.extractionConfidence === 'string') {
    spot.extractionConfidence = data.extractionConfidence
  }

  const targetStudioUsername = normalizeInstagramUsername(data.targetStudioUsername)
  const sourceStudioUsername = normalizeInstagramUsername(data.sourceStudioUsername)
  const artistResidentStudioUsername = normalizeInstagramUsername(data.artistResidentStudioUsername)

  if (targetStudioUsername) {
    spot.targetStudioUsername = targetStudioUsername
  }
  if (sourceStudioUsername) {
    spot.sourceStudioUsername = sourceStudioUsername
  }
  if (artistResidentStudioUsername) {
    spot.artistResidentStudioUsername = artistResidentStudioUsername
  }

  return spot
}

export function getGuestSpotPortfolioImageUrls(guestSpot: GuestSpot): string[] | null {
  const compressedImageUrls = (guestSpot.top4CompressedImageUrls ?? [])
    .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    .slice(0, 3)

  if (compressedImageUrls.length !== 3) {
    return null
  }

  return compressedImageUrls
}
